import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  User,
  BarChart3,
  Briefcase,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { Progress } from "./ui/progress";

export function RoleBasedInsights() {
  return (
    <Tabs defaultValue="investor" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="investor" className="gap-2">
          <User className="h-4 w-4" />
          Investor View
        </TabsTrigger>
        <TabsTrigger value="analyst" className="gap-2">
          <BarChart3 className="h-4 w-4" />
          Analyst View
        </TabsTrigger>
        <TabsTrigger value="advisor" className="gap-2">
          <Briefcase className="h-4 w-4" />
          Advisor View
        </TabsTrigger>
      </TabsList>

      <TabsContent value="investor" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Investment Goals Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Retirement Fund Target</span>
                  <span className="font-medium">68%</span>
                </div>
                <Progress value={68} />
                <p className="text-xs text-muted-foreground">
                  $165,000 / $243,000 goal
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Education Fund</span>
                  <span className="font-medium">45%</span>
                </div>
                <Progress value={45} />
                <p className="text-xs text-muted-foreground">
                  $45,000 / $100,000 goal
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Emergency Fund</span>
                  <span className="font-medium">100%</span>
                </div>
                <Progress value={100} />
                <p className="text-xs text-muted-foreground">Goal achieved! ✓</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Action Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm">Review quarterly rebalancing proposal</p>
                  <p className="text-xs text-muted-foreground">Due in 3 days</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm">Schedule meeting with financial advisor</p>
                  <p className="text-xs text-muted-foreground">Suggested for next week</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm">Review ESG investment opportunities</p>
                  <p className="text-xs text-muted-foreground">New options available</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="analyst" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Research Queue</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium">Tech Sector Analysis</p>
                    <Badge variant="secondary" className="text-xs">High Priority</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Deep dive into semiconductor stocks
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Due: Oct 12, 2025
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium">ESG Fund Evaluation</p>
                    <Badge variant="secondary" className="text-xs">Medium</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Client request for sustainable options
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Due: Oct 15, 2025
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Market Alerts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 border rounded-lg border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-900">
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  AAPL earnings beat estimates
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  EPS: $1.47 vs $1.39 expected
                </p>
              </div>
              <div className="p-3 border rounded-lg border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-900">
                <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                  Fed meeting scheduled
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                  Expected rate decision next Wednesday
                </p>
              </div>
              <div className="p-3 border rounded-lg border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-900">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Sector rotation detected
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Capital flowing from tech to healthcare
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="advisor" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Client Portfolio Review</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Risk Assessment</p>
                  <Badge variant="secondary">Within Target</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Current portfolio risk score: 6.2/10
                </p>
                <p className="text-xs text-muted-foreground">
                  Target risk range: 5.5 - 7.0
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Diversification</p>
                  <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                    Good
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Well-balanced across 5 sectors
                </p>
                <p className="text-xs text-muted-foreground">
                  No single sector {'>'} 25% allocation
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Tax Efficiency</p>
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
                    Review Needed
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Consider tax-loss harvesting opportunity
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recommendations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 border rounded-lg">
                <p className="text-sm font-medium mb-1">Quarterly Rebalancing</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Suggest rebalancing to maintain target allocation. Tech sector
                  slightly overweight.
                </p>
                <Button size="sm" className="w-full">
                  Create Proposal
                </Button>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-sm font-medium mb-1">ESG Integration</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Client expressed interest in sustainable investing. Review ESG
                  fund options.
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Research Options
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>
    </Tabs>
  );
}
